<template>
  <section class="author-profile" aria-labelledby="author-profile-name">
    <div class="author-profile__avatar-frame">
      <img
        :src="profile.profile_photo || imagemPadrao"
        :alt="`Foto de perfil de ${authorName}`"
        class="author-profile__avatar"
        width="112"
        height="112"
      >
    </div>

    <div class="author-profile__content">
      <div class="author-profile__main">
        <div class="author-profile__identity">
          <h1 id="author-profile-name" class="author-profile__name">{{ authorName }}</h1>
          <p v-if="hasNickname" class="author-profile__nickname" :title="formattedNickname">
            {{ formattedNickname }}
          </p>
        </div>

        <div class="author-profile__actions">
          <v-btn
            type="button"
            class="author-profile__button author-profile__button--primary"
            :outlined="!profile.is_follow"
            :disabled="!profile.id"
            @click="follow(profile.id)"
          >
            <span class="author-profile__follow-icon" aria-hidden="true">
              <v-icon
                size="17"
                class="author-profile__follow-icon-glyph author-profile__follow-icon-glyph--plus"
                :class="{ 'author-profile__follow-icon-glyph--active': !profile.is_follow }"
              >
                mdi-account-plus
              </v-icon>
              <v-icon
                size="17"
                class="author-profile__follow-icon-glyph author-profile__follow-icon-glyph--check"
                :class="{ 'author-profile__follow-icon-glyph--active': profile.is_follow }"
              >
                mdi-account-check
              </v-icon>
            </span>
            {{ profile.is_follow ? 'Seguindo' : 'Seguir' }}
          </v-btn>
          <v-btn type="button" class="author-profile__button author-profile__button--secondary">
            Apoiar
          </v-btn>
        </div>

        <nav v-if="socialLinks.length" class="author-profile__socials" aria-label="Redes sociais do autor">
          <v-btn
            v-for="social in socialLinks"
            :key="social.name"
            icon
            :href="social.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="`${social.name} de ${authorName}`"
            class="author-profile__social-link"
          >
            <v-icon size="22">{{ social.icon }}</v-icon>
          </v-btn>
        </nav>
      </div>

      <dl class="author-profile__stats" aria-label="Estatísticas do autor">
        <div class="author-profile__stat">
          <dt>Obras</dt>
          <dd>{{ projectsCount }}</dd>
        </div>
        <div class="author-profile__stat">
          <dt>Seguindo</dt>
          <dd>{{ data.following || 0 }}</dd>
        </div>
        <div class="author-profile__stat">
          <dt>Seguidores</dt>
          <dd>{{ data.followers || 0 }}</dd>
        </div>
      </dl>
    </div>
  </section>
</template>

<script>
import { FollowAuthorService } from '@/server/FollowtService';
import { sanitizeHttpUrl } from '@/utils/safeNavigation';

export default {
  name: 'HeaderAuthor',
  props: {
    data: {
      type: Object,
      required: true,
    },
    projectsCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      imagemPadrao: 'https://thumbs.dreamstime.com/b/vetor-de-ícone-perfil-do-avatar-padrão-foto-usuário-mídia-social-183042379.jpg',
    };
  },
  computed: {
    profile() {
      return this.data.author || {};
    },
    authorName() {
      return this.profile.name || '';
    },
    hasNickname() {
      return String(this.profile.nickname || '').trim().length > 0;
    },
    formattedNickname() {
      return `@${String(this.profile.nickname || '').trim()}`;
    },
    socialLinks() {
      return [
        { name: 'Facebook', icon: 'mdi-facebook', url: this.profile.facebook },
        { name: 'Instagram', icon: 'mdi-instagram', url: this.profile.instagram },
        { name: 'Twitter', icon: 'mdi-twitter', url: this.profile.twitter },
      ]
        .map(social => ({ ...social, url: sanitizeHttpUrl(social.url) }))
        .filter(social => Boolean(social.url));
    },
  },
  methods: {
    async follow(authorId) {
      if (!authorId) return;

      const service = new FollowAuthorService();
      await service.store({ author_id: authorId });
      this.$emit('updated');
    },
  },
};
</script>

<style scoped>
.author-profile {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  padding: 16px;
  color: var(--author-foreground, #fff7ff);
  background: var(--author-surface, #160d1f);
  border: 1px solid var(--author-border, rgba(255, 255, 255, 0.14));
  border-radius: var(--author-radius, 22px);
  box-shadow: var(--author-shadow, 0 18px 48px rgba(0, 0, 0, 0.38));
}

.author-profile__avatar-frame {
  width: 84px;
  height: 84px;
  flex: 0 0 84px;
  overflow: hidden;
  border: 2px solid rgba(255, 207, 90, 0.55);
  border-radius: 22px;
  background: var(--author-surface-soft, #21122f);
  box-shadow: 0 12px 27px rgba(0, 0, 0, 0.23);
}

.author-profile__avatar {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  outline: 1px solid rgba(255, 255, 255, 0.08);
  outline-offset: -1px;
}

.author-profile__content,
.author-profile__main,
.author-profile__identity {
  min-width: 0;
}

.author-profile__content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 12px;
}

.author-profile__name {
  margin: 0;
  color: var(--author-foreground, #fff7ff);
  font-size: clamp(18px, 5.5vw, 24px);
  font-weight: 700;
  line-height: 1.32;
  overflow-wrap: anywhere;
  text-wrap: balance;
}

.author-profile__nickname {
  max-width: 100%;
  margin: 2px 0 0;
  overflow: hidden;
  color: var(--author-muted, #ded0e7) !important;
  font-size: 14px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.author-profile__actions {
  display: flex;
  gap: 6px;
  width: 100%;
  margin-top: 10px;
}

.author-profile__button.v-btn {
  min-width: 0;
  min-height: 40px;
  flex: 1 1 0;
  margin: 0;
  padding: 0 7px !important;
  border-radius: 20px;
  color: #fff !important;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: none;
  box-shadow: none;
  transition-property: transform, box-shadow, background-color;
  transition-duration: 180ms;
}

.author-profile__button--primary.v-btn {
  border-color: var(--author-primary, #4e008d) !important;
  background: var(--author-primary, #4e008d) !important;
  box-shadow: 0 10px 24px rgba(78, 0, 141, 0.38);
}

.author-profile__button--secondary.v-btn {
  border: 1px solid var(--author-border, rgba(255, 255, 255, 0.14));
  background: rgba(255, 255, 255, 0.1) !important;
}

.author-profile__follow-icon {
  position: relative;
  display: inline-flex;
  width: 17px;
  height: 17px;
  margin-right: 7px;
  flex: 0 0 17px;
}

.author-profile__follow-icon-glyph.v-icon {
  position: absolute;
  inset: 0;
  color: currentColor;
  opacity: 0;
  filter: blur(4px);
  transform: scale(0.25);
  transition-property: opacity, filter, transform;
  transition-duration: 180ms;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);
}

.author-profile__follow-icon-glyph--active.v-icon {
  opacity: 1;
  filter: blur(0);
  transform: scale(1);
}

.author-profile__socials {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.author-profile__social-link.v-btn {
  width: 40px;
  height: 40px;
  margin: 0;
  color: #fff7ff !important;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--author-border, rgba(255, 255, 255, 0.14));
  transition-property: transform, background-color, border-color;
  transition-duration: 180ms;
}

.author-profile__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
  width: 100%;
  min-width: 0;
  margin: 0;
}

.author-profile__stat {
  min-width: 0;
  padding: 8px 2px;
  text-align: center;
  background: var(--author-background-elevated, #0d0712);
  border: 1px solid var(--author-border, rgba(255, 255, 255, 0.14));
  border-radius: 18px;
}

.author-profile__stat dt {
  color: var(--author-muted, #ded0e7);
  font-size: clamp(7px, 2.2vw, 10px);
  font-weight: 600;
  line-height: 14px;
  letter-spacing: 0.03em;
  overflow-wrap: anywhere;
  text-transform: uppercase;
}

.author-profile__stat dd {
  min-width: 0;
  margin: 2px 0 0;
  color: var(--author-foreground, #fff7ff);
  font-size: clamp(10px, 2.8vw, 13px);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  line-height: 28px;
  white-space: nowrap;
}

.author-profile__button:focus-visible,
.author-profile__social-link:focus-visible {
  outline: 3px solid var(--author-focus, #ffcf5a);
  outline-offset: 3px;
}

.author-profile__button:active,
.author-profile__social-link:active {
  transform: scale(0.96);
}

@media (min-width: 640px) {
  .author-profile {
    padding: 24px;
  }

  .author-profile__stat {
    padding: 12px;
  }

  .author-profile__avatar-frame {
    width: 96px;
    height: 96px;
    flex-basis: 96px;
  }

  .author-profile__actions {
    width: max-content;
    gap: 8px;
  }

  .author-profile__button.v-btn {
    min-width: 104px;
    padding: 0 16px !important;
  }
}

@media (max-width: 767px) {
  .author-profile {
    --author-mobile-avatar-size: 84px;
    --author-mobile-first-row-size: 36px;
    --author-mobile-actions-size: 32px;
    --author-mobile-row-gap: 4px;

    display: grid;
    grid-template-columns: var(--author-mobile-avatar-size) minmax(0, 1fr);
    grid-template-areas: 'avatar content';
    column-gap: 12px;
  }

  .author-profile__content {
    display: grid;
    grid-area: content;
    grid-template-columns: minmax(44px, 0.7fr) minmax(126px, 1.4fr);
    grid-template-rows: auto auto auto;
    grid-template-areas:
      'identity stats'
      'actions actions'
      'socials socials';
    align-items: center;
    column-gap: 6px;
    row-gap: var(--author-mobile-row-gap);
  }

  .author-profile__main {
    display: contents;
  }

  .author-profile__avatar-frame {
    grid-area: avatar;
  }

  .author-profile__identity {
    grid-area: identity;
  }

  .author-profile__name {
    overflow: hidden;
    font-size: clamp(16px, 4.8vw, 22px);
    line-height: 20px;
    text-overflow: ellipsis;
    text-wrap: nowrap;
    white-space: nowrap;
  }

  .author-profile__nickname {
    font-size: 11.5px;
    line-height: 14px;
  }

  .author-profile__stats {
    grid-area: stats;
    gap: 3px;
  }

  .author-profile__actions {
    grid-area: actions;
    width: 100%;
    margin-top: calc(
      var(--author-mobile-avatar-size)
      - var(--author-mobile-first-row-size)
      - var(--author-mobile-actions-size)
      - var(--author-mobile-row-gap)
    );
  }

  .author-profile__button.v-btn {
    height: 32px;
    min-width: 0;
    min-height: 32px;
    padding: 0 5px !important;
    border-radius: 16px;
    font-size: 11.5px;
    letter-spacing: 0.02em;
  }

  .author-profile__follow-icon {
    width: 15px;
    height: 15px;
    margin-right: 4px;
    flex-basis: 15px;
  }

  .author-profile__follow-icon-glyph.v-icon {
    width: 15px;
    height: 15px;
    font-size: 15px !important;
  }

  .author-profile__socials {
    grid-area: socials;
    margin-top: 0;
  }

  .author-profile__stat {
    padding: 4px 1px;
    border-radius: 12px;
  }

  .author-profile__stat dt {
    font-size: clamp(5.5px, 1.8vw, 8px);
    line-height: 10px;
    letter-spacing: 0.01em;
    overflow-wrap: normal;
    white-space: nowrap;
  }

  .author-profile__stat dd {
    margin-top: 1px;
    font-size: clamp(9px, 2.4vw, 11px);
    line-height: 15px;
  }
}

@media (max-width: 359px) {
  .author-profile {
    --author-mobile-avatar-size: 72px;

    column-gap: 8px;
    padding: 12px;
  }

  .author-profile__avatar-frame {
    width: 72px;
    height: 72px;
    flex-basis: 72px;
  }

  .author-profile__content {
    grid-template-columns: minmax(44px, 0.55fr) minmax(126px, 1.45fr);
    grid-template-rows: auto auto auto;
  }
}

@media (min-width: 640px) and (max-width: 767px) {
  .author-profile {
    --author-mobile-avatar-size: 96px;
  }

  .author-profile__content {
    grid-template-rows: auto auto auto;
  }
}

@media (min-width: 768px) {
  .author-profile {
    align-items: center;
    gap: 20px;
  }

  .author-profile__avatar-frame {
    width: 112px;
    height: 112px;
    flex-basis: 112px;
  }

  .author-profile__content {
    flex-direction: row;
    align-items: center;
    gap: 20px;
  }

  .author-profile__main {
    flex: 1 1 auto;
  }

  .author-profile__name {
    font-size: 30px;
    line-height: 36px;
  }

  .author-profile__stat dd {
    font-size: clamp(13px, 1.5vw, 20px);
  }

  .author-profile__stats {
    width: 300px;
    flex: 0 0 300px;
  }
}

@media (min-width: 768px) and (max-width: 850px) {
  .author-profile__stat {
    padding: 8px 4px;
  }

  .author-profile__stat dd {
    font-size: 12px;
  }
}

@media (min-width: 1024px) {
  .author-profile__stats {
    width: 360px;
    flex-basis: 360px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .author-profile__button:hover,
  .author-profile__social-link:hover {
    transform: translateY(-2px);
  }

  .author-profile__social-link:hover {
    border-color: rgba(255, 207, 90, 0.7);
    background: rgba(255, 255, 255, 0.1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .author-profile__button.v-btn,
  .author-profile__social-link.v-btn,
  .author-profile__follow-icon-glyph.v-icon {
    transition-duration: 0ms;
  }

  .author-profile__button:hover,
  .author-profile__button:active,
  .author-profile__social-link:hover,
  .author-profile__social-link:active {
    transform: none;
  }
}
</style>
